/**
 * Контрагенты выборки: обороты, период и город. Один компонент на два места —
 * раздел «Контрагенты» и вкладку «Аналитика» у операций, чтобы город
 * привязывали одинаково там и там.
 *
 * Сверху — итог по городам: какого города эта аренда, этот подрядчик.
 * Город выбирают прямо в строке (право `pnl_edit`); он один на контрагента
 * и сразу виден во всех выборках. В выборе — только кассовые города CRM
 * (`isKassa=1`) в порядке CRM (разметка владельца 30.09.26).
 *
 * Заметка — строка текста у контрагента: кто это, куда разносить. По
 * заметкам потом пишут правила. Сохраняется по Enter или уходу из поля.
 *
 * Спека: `docs/specs/2026-09-30-counterparty-location-design.md`.
 */
import { useEffect, useRef, useState } from "react";

import { type ComboOption, Combobox } from "@/components/Combobox";
import { PNL_EDIT, useCan } from "@/lib/access";
import {
  type Counterparty,
  type Location,
  loadLocations,
  money,
  setCounterpartyLocation,
  setCounterpartyNote,
} from "@/lib/pnl";

const TH = "border-b border-border px-2.5 py-2";
const TD = "border-b border-border px-2.5 py-1.5";
const NO_CITY = "без города";

function day(iso: string): string {
  return iso.split("-").reverse().join(".");
}

type Result = { truncated: boolean; rows: Counterparty[] };

export function CounterpartyTable(props: {
  result: Result;
  onResult: (result: Result) => void;
  onOpen: (cp: Counterparty) => void;
  onFailure: (message: string) => void;
}) {
  const { result } = props;
  const canEdit = useCan(PNL_EDIT);
  const [locations, setLocations] = useState<Location[]>([]);

  // Города грузим один раз на экран.
  const { onFailure } = props;
  useEffect(() => {
    loadLocations()
      .then(setLocations)
      .catch((error: Error) => onFailure(error.message));
  }, [onFailure]);

  const bind = (cp: Counterparty, locationId: number | null) => {
    setCounterpartyLocation(cp, locationId)
      .then((saved) =>
        props.onResult({
          ...result,
          rows: result.rows.map((r) =>
            r.inn === cp.inn && r.name === cp.name
              ? { ...r, location_id: saved.location_id, location_name: saved.location_name }
              : r,
          ),
        }),
      )
      .catch((error: Error) => props.onFailure(error.message));
  };

  const saveNote = (cp: Counterparty, text: string) => {
    setCounterpartyNote(cp, text)
      .then((saved) =>
        props.onResult({
          ...result,
          rows: result.rows.map((r) => (r.inn === cp.inn && r.name === cp.name ? { ...r, note: saved.note } : r)),
        }),
      )
      .catch((error: Error) => props.onFailure(error.message));
  };

  return (
    <>
      <div className="text-[12px] text-fg-muted">
        Контрагентов: {result.rows.length}
        {result.truncated ? " — показаны самые крупные по обороту; уточните поиском" : ""}
      </div>
      <ByCity rows={result.rows} truncated={result.truncated} />
      <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
        <table className="w-full border-collapse text-[12.5px]">
          <thead>
            <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
              <th className={`${TH} text-left`}>контрагент</th>
              <th className={`${TH} text-left`}>город</th>
              <th className={`${TH} text-left`}>заметка</th>
              <th className={`${TH} text-right`}>операций</th>
              <th className={`${TH} text-right`}>приход</th>
              <th className={`${TH} text-right`}>расход</th>
              <th className={`${TH} text-left`}>период</th>
            </tr>
          </thead>
          <tbody>
            {result.rows.map((cp) => {
              // Без ИНН и имени привязать город и заметку не к чему; операции открыть можно.
              const named = Boolean(cp.inn || cp.name);
              return (
                <tr
                  data-annot={`контрагент ${cp.inn ?? cp.name}`}
                  key={cp.inn ?? `name:${cp.name}`}
                  onClick={() => props.onOpen(cp)}
                  className="cursor-pointer hover:bg-bg-hover"
                  title="Операции с контрагентом в этой выборке"
                >
                  <td className={`${TD} max-w-[420px]`}>
                    <div className="truncate">{cp.name || "(без названия)"}</div>
                    <div className="text-[11px] text-fg-faint">{cp.inn ? `ИНН ${cp.inn}` : "без ИНН"}</div>
                  </td>
                  <td className={TD} onClick={(e) => e.stopPropagation()}>
                    {canEdit && named ? (
                      <Combobox
                        size="sm"
                        className="w-[160px]"
                        value={cp.location_id ? String(cp.location_id) : ""}
                        onChange={(v) => bind(cp, v ? Number(v) : null)}
                        options={cityOptions(locations, cp)}
                      />
                    ) : (
                      <span className={cp.location_name ? "" : "text-fg-faint"}>{cp.location_name ?? "—"}</span>
                    )}
                  </td>
                  <td className={`${TD} min-w-[220px]`} onClick={(e) => e.stopPropagation()}>
                    {canEdit && named ? (
                      <NoteInput value={cp.note} onSave={(text) => saveNote(cp, text)} />
                    ) : (
                      <span className={cp.note ? "" : "text-fg-faint"}>{cp.note || "—"}</span>
                    )}
                  </td>
                  <td className={`${TD} text-right tabular-nums`}>{cp.operations}</td>
                  <td className={`${TD} whitespace-nowrap text-right tabular-nums text-ok`}>
                    {Number(cp.total_in) ? `+${money(cp.total_in)}` : "—"}
                  </td>
                  <td className={`${TD} whitespace-nowrap text-right tabular-nums`}>
                    {Number(cp.total_out) ? `−${money(cp.total_out)}` : "—"}
                  </td>
                  <td className={`${TD} whitespace-nowrap tabular-nums text-fg-muted`}>
                    {cp.date_first === cp.date_last ? day(cp.date_first) : `${day(cp.date_first)} — ${day(cp.date_last)}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

/**
 * Поле заметки: правят на месте, сохраняют Enter или уход из поля; Esc — отмена.
 * Рамка видна всегда: поле должно читаться как поле, без поиска по наведению.
 */
function NoteInput(props: { value: string; onSave: (text: string) => void }) {
  const [draft, setDraft] = useState(props.value);
  // Esc: blur() приходит раньше, чем сброс черновика — сохранять нечего.
  const cancelled = useRef(false);
  useEffect(() => setDraft(props.value), [props.value]);
  const commit = () => {
    if (cancelled.current) cancelled.current = false;
    else if (draft.trim() !== props.value) props.onSave(draft);
  };
  return (
    <input
      className="h-7 w-full rounded-[var(--radius-ui)] border border-border bg-bg px-1.5 text-[12px] outline-none placeholder:text-fg-faint focus:border-border-strong"
      placeholder="кто это, куда разносить"
      maxLength={2000}
      value={draft}
      title={draft || undefined}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          cancelled.current = true;
          setDraft(props.value);
          e.currentTarget.blur();
        }
      }}
    />
  );
}

/** Пункты города: «— город —», кассовые города и — если контрагент уже
 * привязан к некассовому — его город, чтобы привязка не выглядела пустой. */
function cityOptions(locations: Location[], cp: Counterparty): ComboOption[] {
  const options: ComboOption[] = [{ value: "", label: "— город —" }];
  if (cp.location_id && cp.location_name && !locations.some((l) => l.id === cp.location_id)) {
    options.push({ value: String(cp.location_id), label: cp.location_name });
  }
  return [...options, ...locations.map((l) => ({ value: String(l.id), label: l.name }))];
}

/** Итог выборки по городам: сколько ушло и пришло по каждому. */
function ByCity(props: { rows: Counterparty[]; truncated: boolean }) {
  const cities = new Map<string, { total_in: number; total_out: number; counterparties: number }>();
  for (const cp of props.rows) {
    const key = cp.location_name ?? NO_CITY;
    const city = cities.get(key) ?? { total_in: 0, total_out: 0, counterparties: 0 };
    city.total_in += Number(cp.total_in);
    city.total_out += Number(cp.total_out);
    city.counterparties += 1;
    cities.set(key, city);
  }
  // Город один — и тот «без города»: итог ничего не добавляет к таблице.
  if (cities.size === 0 || (cities.size === 1 && cities.has(NO_CITY))) return null;
  const sorted = [...cities.entries()].sort(
    ([a, x], [b, y]) => Number(a === NO_CITY) - Number(b === NO_CITY) || y.total_out + y.total_in - (x.total_out + x.total_in),
  );
  return (
    <div className="flex flex-wrap gap-2 text-[12px]">
      {sorted.map(([name, city]) => (
        <div
          key={name}
          data-annot={`город ${name}`}
          className={`rounded-[var(--radius-ui)] border border-border px-2.5 py-1.5 ${name === NO_CITY ? "text-fg-muted" : ""}`}
        >
          <div className="font-medium">{name}</div>
          <div className="tabular-nums text-fg-muted">
            {city.total_out ? `−${money(city.total_out)}` : ""}
            {city.total_in && city.total_out ? " · " : ""}
            {city.total_in ? <span className="text-ok">+{money(city.total_in)}</span> : ""}
            <span className="ml-1.5 text-fg-faint">· контрагентов {city.counterparties}</span>
          </div>
        </div>
      ))}
      {props.truncated ? <div className="self-center text-fg-faint">— по показанным контрагентам</div> : null}
    </div>
  );
}
